import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    TextField,
    Typography,
    Button,
    MenuItem,
    useTheme,
    IconButton,
    CircularProgress,
    Alert,
    Drawer,
    ToggleButton,
    ToggleButtonGroup,
    Collapse,
    Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Banknote, CheckCircle2, ChevronDown, CreditCard, Plus, QrCode, X, XCircle } from 'lucide-react';
import { httpClient } from '../../../common/httpclient';
import DialogForm from '../../../component/dialog/DialogForm';
import useIsMobileShell from '../../../hooks/useIsMobileShell';
import { PaymentScreen, DiscountInput, MobileAlertDialog } from '../../../component/sale/MobileSaleView';
import { formatCurrency } from '../../../common/formatters';
import { standaloneFieldSx, standaloneFloatingSelectSx } from '../../../common/fieldStyles';

/**
 * Drawer de pagamento (faturamento de OS / finalização de venda) —
 * DESIGN-SYSTEM.md §5.6 (side drawer) / §5.7 (acordeão de métodos de
 * pagamento). Segue exatamente o mesmo padrão visual do drawer de
 * pagamento da POS (`Sale.js`): `Drawer` real (não overlay + `Slide`
 * manual) para herdar o override global de `MuiDrawer` do tema, mesma
 * largura (460px / 92vw), mesmo cabeçalho, mesmo acordeão de métodos e
 * mesma lista de pagamentos adicionados.
 *
 * Este componente NÃO cria seu próprio `ThemeProvider`/`createTheme` — ele
 * herda o tema global, assim como `Sale.js`.
 */

// Styled components — espelham exatamente os equivalentes de Sale.js
// (PayMethodCard/PayMethodHead/PayMethodIcon/PaidRow), usando tokens do
// tema (sem hex/gradiente hardcoded, sem ternários de dark mode manuais).
const PayMethodCard = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    marginBottom: 10,
    overflow: 'hidden'
}));

const PayMethodHead = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    cursor: 'pointer'
}));

const PayMethodIcon = styled(Box)(({ theme }) => ({
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: theme.palette.info.bg,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
}));

const PaidRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    background: theme.palette.background.subtle,
    borderRadius: 10,
    marginBottom: 8,
    fontSize: 12.5
}));

const RowIconButton = styled(IconButton)(({ theme }) => ({
    width: 28,
    height: 28,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.disabled,
    '&:hover': { backgroundColor: theme.palette.background.subtle },
    '&.Mui-disabled': { opacity: 0.5 }
}));

/** API pode retornar objeto ou array com um único objeto: [{ "1": "R$ 30,00", "2": "R$ 15,00" }] */
function normalizeInstallmentsResponse(data) {
    const payload = Array.isArray(data) && data.length > 0 ? data[0] : data;
    if (!payload || typeof payload !== 'object') return [];
    return Object.entries(payload)
        .map(([key, installmentLabel]) => ({
            count: Number(key),
            installmentLabel: typeof installmentLabel === 'string' ? installmentLabel : String(installmentLabel ?? '')
        }))
        .filter(({ count }) => Number.isFinite(count) && count > 0)
        .sort((a, b) => a.count - b.count);
}

function brAmountToNumber(amountStr) {
    if (!amountStr || typeof amountStr !== 'string') return NaN;
    const normalized = amountStr.replace(/\./g, '').replace(',', '.');
    return Number.parseFloat(normalized);
}

const PaymentModal = ({
    open,
    onClose,
    total,
    totalWithDiscount = 0,
    onFinish,
    entityType = 'service_order', // 'service_order' ou 'sale'
    entityId
}) => {
    const theme = useTheme();
    const isMobile = useIsMobileShell();
    // Desconto digitado no layout mobile (só é aplicado ao tocar em "Aplicar desconto")
    const [mobileDiscountInput, setMobileDiscountInput] = useState('');

    // Estados de pagamento
    const [paymentType, setPaymentType] = useState('single');
    const [creditValue, setCreditValue] = useState('');
    const [debitValue, setDebitValue] = useState('');
    const [pixValue, setPixValue] = useState('');
    const [cashValue, setCashValue] = useState('');
    const [selectedMachine, setSelectedMachine] = useState('');
    const [pixType, setPixType] = useState('PIXKEY');
    /** @type {{ count: number, installmentLabel: string }[]} */
    const [installmentOptions, setInstallmentOptions] = useState([]);
    const [installmentsLoading, setInstallmentsLoading] = useState(false);
    const [selectedInstallments, setSelectedInstallments] = useState(1);

    // Estados de UI
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);
    const [showCreditCard, setShowCreditCard] = useState(false);
    const [showDebitCard, setShowDebitCard] = useState(false);
    const [showPix, setShowPix] = useState(false);
    const [showCash, setShowCash] = useState(false);

    // Estados de dados
    const [payments, setPayments] = useState([]);
    const [machineCards, setMachineCards] = useState([]);
    const [remainingValue, setRemainingValue] = useState(total);
    const [discount, setDiscount] = useState({ applied: false, value: totalWithDiscount, percentage: 0, applyed_discount: '0,00' });
    const [errorMessage, setErrorMessage] = useState('');

    // Estados de diálogo
    const [openDialog, setOpenDialog] = useState(false);

    // Estado de loading para o botão finalizar
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);

    // Carregar maquininhas
    useEffect(() => {
        const loadMachineCards = async () => {
            try {
                const response = await httpClient.get('/cardmachines');
                setMachineCards(response.data);
                if (response.data.length > 0) {
                    setSelectedMachine(response.data[0].id);
                }
            } catch (error) {
                console.error('Erro ao carregar maquininhas:', error);
            }
        };

        if (open) {
            loadMachineCards();
            // Resetar valores quando abrir
            setCreditValue(total);
            setDebitValue(total);
            setPixValue(total);
            setCashValue(total);
            setRemainingValue(total);
            setPayments([]);
            setPaymentType('single'); // Reset para pagamento único por padrão
            setShowPaymentOptions(false); // Reset das opções de pagamento
            setErrorMessage(''); // Limpar mensagem de erro
            setInstallmentOptions([]);
            setSelectedInstallments(1);
            setMobileDiscountInput('');
        }
    }, [open, total]);

    const getCreditAmountString = useCallback(() => {
        if (discount.applied && paymentType === 'single') {
            return discount.value;
        }
        return creditValue;
    }, [discount.applied, discount.value, paymentType, creditValue]);

    useEffect(() => {
        // No mobile não há acordeão de crédito: as parcelas são recalculadas sempre que valor/maquininha mudam.
        if (!open || !(showCreditCard || isMobile) || !selectedMachine) {
            return undefined;
        }

        const amountStr = getCreditAmountString();
        const amountNum = brAmountToNumber(amountStr);
        if (!Number.isFinite(amountNum) || amountNum <= 0) {
            setInstallmentOptions([]);
            setSelectedInstallments(1);
            return undefined;
        }

        const handle = setTimeout(async () => {
            setInstallmentsLoading(true);
            try {
                const response = await httpClient.get('/orderservices/installments', {
                    params: {
                        amount: amountStr,
                        card_machine_id: selectedMachine
                    }
                });
                const options = normalizeInstallmentsResponse(response.data);
                setInstallmentOptions(options);
                setSelectedInstallments((prev) => {
                    const counts = options.map((o) => o.count);
                    const n = Number(prev);
                    return counts.includes(n) ? n : (counts[0] ?? 1);
                });
            } catch (error) {
                console.error('Erro ao carregar parcelas:', error);
                setInstallmentOptions([]);
                setSelectedInstallments(1);
            } finally {
                setInstallmentsLoading(false);
            }
        }, 400);

        return () => clearTimeout(handle);
    }, [open, showCreditCard, isMobile, selectedMachine, getCreditAmountString]);



    const handleAddPayment = (type, value, installments = 1, installmentValueLabel = null) => {
        const payment = {
            type,
            value,
            installments: type === 'CRÉDITO' ? Number(installments) : 1,
            machine: selectedMachine,
            ...(type === 'CRÉDITO' && installmentValueLabel ? { installmentValueLabel } : {})
        };

        payments.push(payment);

        //setPayments(prev => [...prev, payment]);

        // Resetar campos
        setCreditValue('');
        setDebitValue('');
        setPixValue('');
        setCashValue('');
        setShowCreditCard(false);
        setShowDebitCard(false);
        setShowPix(false);
        setShowCash(false);
        setShowPaymentOptions(false);

        ;
        if (paymentType === 'multiple') {
            handleGetRemainingValue(payments);
        }
    };

    const handleRemovePayment = (index) => {
        setPayments(prev => prev.filter((_, i) => i !== index));
    };



    const handleFinishPayment = async () => {
        if (payments.length === 0 || (payments.length > 1 && paymentType === 'multiple' && remainingValue !== '0,00')) {
            return;
        }

        try {
            setIsProcessingPayment(true);
            setErrorMessage(''); // Limpar mensagem de erro anterior
            const paymentData = {
                order_service_id: entityId,
                payments: payments.map(payment => ({
                    method: payment.type,
                    amount: payment.value,
                    installments: payment.installments,
                    machine_id: payment.machine
                })),
                is_split_payment: paymentType === 'multiple'
            };

            setOpenDialog(false);
            const response = await httpClient.post(`/orderservices/${entityId}/payment`, paymentData);

            if (response.status === 200) {
                handleGetRemainingValue(payments);
                if (onFinish) {
                    onFinish();
                }
                onClose();
            } else {
                setOpenDialog(false);
                setErrorMessage(response.data?.message || response.data || 'Erro ao processar pagamento');
            }
        } catch (error) {
            console.error('Erro ao finalizar pagamento:', error);
            setOpenDialog(false);
            // Capturar mensagem de erro da resposta
            const errorMessage = error.response?.data?.message
                || error.response?.data?.error
                || error.response?.data
                || error.message
                || 'Erro ao processar pagamento. Tente novamente.';
            setErrorMessage(errorMessage);
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const handleGetRemainingValue = async (paymentsList) => {
        const payload = paymentsList.map((p) => ({
            type: p.type,
            value: p.value,
            installments: p.installments,
            machine: p.machine
        }));
        const response = await httpClient.post(`/orderservices/${entityId}/remainder`, payload);
        setRemainingValue(response.data.remainder);
    };

    const handleApplyDiscount = async (valueOverride) => {
        var discountInput = {

            discount: valueOverride !== undefined ? valueOverride : discount.value
        }
        const response = await httpClient.post(`/orderservices/${entityId}/discount/apply`, discountInput);
        //setDiscount(response.data);
        ;
        setDiscount({
            applied: true,
            value: response.data.remainder,
            applyed_discount: response.data.applyed_discount_value,
            percentage: 0
        });
        setRemainingValue(response.data.remainder);

    };

    // Para pagamento múltiplo, as opções devem estar sempre disponíveis
    // Para pagamento único, desabilitar quando já há um pagamento
    const disabledPayment = paymentType === 'single' && payments.length > 0;

    // Layout mobile: reaproveita a tela "Finalizar Pagamento" da venda (`PaymentScreen`),
    // mantendo aqui todo o estado, as regras e as chamadas de API do faturamento da OS.
    if (isMobile) {
        const mobileTypeMap = { 'CRÉDITO': 'credit', 'DÉBITO': 'debit', PIXKEY: 'pix_key', PIXQRCODE: 'pix_qrcode', DINHEIRO: 'cash' };
        const baseTotal = discount.applied ? String(discount.value) : String(total);
        const mobileRemaining = paymentType === 'multiple' ? String(remainingValue) : (payments.length > 0 ? '0,00' : baseTotal);
        const mobilePayments = payments.map((p) => ({
            type: mobileTypeMap[p.type] || p.type,
            value: p.value,
            installment: `${p.installments}x${p.installmentValueLabel ? ` ${p.installmentValueLabel}` : ''}`
        }));
        const canFinish = payments.length > 0
            && !(payments.length > 1 && paymentType === 'multiple' && remainingValue !== '0,00')
            && !isProcessingPayment;
        const discountInputValid = Boolean(mobileDiscountInput) && mobileDiscountInput !== '0,00' && mobileDiscountInput !== '0,00%';

        const handleMobileApplyDiscount = async () => {
            try {
                setErrorMessage('');
                await handleApplyDiscount(mobileDiscountInput);
            } catch (error) {
                setErrorMessage(error?.response?.data?.error || error?.response?.data?.message || 'Erro ao aplicar desconto');
            }
        };

        const handleMobilePaymentType = (value) => {
            if (!value) return;
            setPaymentType(value);
            if (value === 'single') {
                setCreditValue(total);
                setDebitValue(total);
                setPixValue(total);
                setCashValue(total);
            } else {
                setCreditValue('');
                setDebitValue('');
                setPixValue('');
                setCashValue('');
            }
        };

        const mobileExtras = (
            <Box
                sx={{
                    mx: 2.5,
                    mb: 2.5,
                    p: 2,
                    flexShrink: 0,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.subtle',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={paymentType}
                    onChange={(e, value) => handleMobilePaymentType(value)}
                >
                    <ToggleButton value="single" sx={{ flex: 1, minHeight: 44, whiteSpace: 'nowrap', px: 1 }}>Pagamento único</ToggleButton>
                    <ToggleButton value="multiple" sx={{ flex: 1, minHeight: 44, whiteSpace: 'nowrap', px: 1 }}>Pagamento múltiplo</ToggleButton>
                </ToggleButtonGroup>

                {discount.applied ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Desconto aplicado</Typography>
                        <Button
                            color="error"
                            onClick={() => setDiscount({ applied: false, value: '0,00', percentage: 0 })}
                        >
                            Remover desconto
                        </Button>
                    </Box>
                ) : (
                    <>
                        <DiscountInput label="Desconto (opcional)" onChange={(value) => setMobileDiscountInput(value)} />
                        <Button
                            fullWidth
                            variant="outlined"
                            disabled={!discountInputValid}
                            onClick={handleMobileApplyDiscount}
                        >
                            Aplicar desconto
                        </Button>
                    </>
                )}
            </Box>
        );

        return (
            <>
                <Drawer
                    anchor="right"
                    open={open}
                    onClose={onClose}
                    PaperProps={{ sx: { width: '100%', maxWidth: '100%', borderRadius: 0, backgroundImage: 'none' } }}
                >
                    <PaymentScreen
                        title={entityType === 'service_order' ? 'Faturar Ordem' : 'Finalizar Venda'}
                        finishLabel={entityType === 'service_order' ? 'Faturar ordem' : 'Finalizar venda'}
                        saleState={{
                            id: entityId,
                            subtotal: String(total),
                            total_item_discount: '0,00',
                            discount_applied: discount.applied ? discount.applyed_discount : '0,00',
                            remaining_value: mobileRemaining
                        }}
                        payments={mobilePayments}
                        machineCards={machineCards}
                        installments={installmentOptions.map((o) => o.count)}
                        selectedMachine={selectedMachine}
                        // PaymentScreen limpa a maquininha após adicionar; a OS sempre envia uma maquininha, então ignoramos o reset.
                        setSelectedMachine={(id) => { if (id) setSelectedMachine(id); }}
                        uiState={{ installments: selectedInstallments }}
                        setUiState={(updater) => {
                            const next = typeof updater === 'function' ? updater({ installments: selectedInstallments }) : updater;
                            if (next && next.installments !== undefined && next.installments !== '') {
                                setSelectedInstallments(Number(next.installments));
                            }
                        }}
                        discountState={{ applied: discount.applied }}
                        creditValue={creditValue}
                        setCreditValue={setCreditValue}
                        debitValue={debitValue}
                        setDebitValue={setDebitValue}
                        pixValue={pixValue}
                        setPixValue={setPixValue}
                        cashValue={cashValue}
                        setCashValue={setCashValue}
                        loading={isProcessingPayment}
                        getPaymentBaseTotal={() => baseTotal}
                        onBack={onClose}
                        onAddPayment={(type, value, installments) => {
                            if (paymentType === 'single' && payments.length > 0) {
                                throw new Error('No pagamento único só é possível um pagamento. Remova o existente ou use pagamento múltiplo.');
                            }
                            // Pagamento único sempre paga o total (igual ao layout desktop).
                            const finalValue = paymentType === 'single' ? baseTotal : value;
                            const opt = installmentOptions.find((o) => o.count === Number(installments));
                            handleAddPayment(type, finalValue, Number(installments), opt?.installmentLabel ?? null);
                        }}
                        onRemovePayment={handleRemovePayment}
                        onComplete={() => setOpenDialog(true)}
                        // As parcelas são recalculadas pelo efeito acima (valor/maquininha).
                        onGetInstallments={() => Promise.resolve()}
                        onChangeMachine={(id) => setSelectedMachine(id)}
                        // Mobile sem toasts: só erro/aviso vira modal; sucesso (ex.: "Pagamento adicionado") é visual na própria lista.
                        toast={(message, severity = 'info') => {
                            if (severity !== 'error' && severity !== 'warning') return;
                            setErrorMessage(message || 'Ocorreu um erro. Tente novamente.');
                        }}
                        afterSummary={mobileExtras}
                        canFinishOverride={canFinish}
                    />
                </Drawer>

                {/* Erros do faturamento no mobile: modal "Atenção" único (sem toast) */}
                <MobileAlertDialog
                    open={Boolean(errorMessage)}
                    message={typeof errorMessage === 'string' ? errorMessage : 'Erro ao processar pagamento'}
                    onClose={() => setErrorMessage('')}
                />

                <DialogForm
                    iconType="warning"
                    title="Atenção"
                    content={entityType === 'service_order' ? 'Deseja realmente faturar esta ordem de serviço?' : 'Deseja realmente finalizar esta venda?'}
                    confirmButtonText={entityType === 'service_order' ? 'Sim, faturar' : undefined}
                    onSubmit={handleFinishPayment}
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    disabled={isProcessingPayment}
                />
            </>
        );
    }

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{ sx: { width: 460, maxWidth: '92vw', display: 'flex', flexDirection: 'column' } }}
            >
                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: (t) => `1px solid ${t.palette.divider}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>
                            {entityType === 'service_order' ? 'Faturar ordem de serviço' : 'Finalizar venda'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Escolha as formas de pagamento</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '10px' }}>
                        <X size={16} />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        value={paymentType}
                        onChange={(e, value) => {
                            if (!value) return;
                            setPaymentType(value);
                            if (value === 'single') {
                                setCreditValue(total);
                                setDebitValue(total);
                                setPixValue(total);
                                setCashValue(total);
                            } else if (value === 'multiple') {
                                setCreditValue('');
                                setDebitValue('');
                                setPixValue('');
                                setCashValue('');
                            }
                        }}
                        sx={{ mb: 2, width: '100%' }}
                    >
                        <ToggleButton value="single" sx={{ flex: 1 }}>Pagamento único</ToggleButton>
                        <ToggleButton value="multiple" sx={{ flex: 1 }}>Pagamento múltiplo</ToggleButton>
                    </ToggleButtonGroup>

                    {!discount.applied && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                            <TextField
                                label="Desconto"
                                helperText="Opcional — valor fixo ou percentual (ex.: 10,00 ou 10%)"
                                fullWidth
                                sx={standaloneFieldSx}
                                placeholder="Ex.: 10,00 ou 10%"
                                value={discount.value}
                                onChange={(e) => setDiscount(prev => ({ ...prev, value: e.target.value }))}
                            />
                            <RowIconButton onClick={() => handleApplyDiscount()} sx={{ width: 52, height: 52 }}>
                                <Plus size={16} />
                            </RowIconButton>
                        </Box>
                    )}

                    <Box sx={{ p: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '12px', backgroundColor: 'background.subtle', mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase' }}>Subtotal</Typography>
                            <Typography fontWeight={800} color="primary.main">R$ {total}</Typography>
                        </Box>
                        {discount.applied && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" fontWeight={700} color="error.main" sx={{ textTransform: 'uppercase' }}>Desconto</Typography>
                                    <Tooltip title="Remover desconto">
                                        <IconButton
                                            size="small"
                                            onClick={() => setDiscount({ applied: false, value: '0,00', percentage: 0 })}
                                        >
                                            <XCircle size={14} color={theme.palette.error.main} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography fontWeight={800} color="error.main">- R$ {discount.applyed_discount}</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase' }}>Total</Typography>
                            <Typography fontWeight={800} color={discount.applied ? 'success.main' : 'text.primary'}>
                                R$ {discount.applied ? discount.value : total}
                            </Typography>
                        </Box>
                        {remainingValue !== '0,00' && paymentType === 'multiple' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 1, borderTop: (t) => `1px dashed ${t.palette.divider}` }}>
                                <Typography fontWeight={700}>Falta receber</Typography>
                                <Typography fontWeight={800} color="error.main">R$ {remainingValue}</Typography>
                            </Box>
                        )}
                    </Box>

                    {!disabledPayment && (
                        <Box
                            data-testid="toggle-payment-methods"
                            onClick={() => setShowPaymentOptions(!showPaymentOptions)}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1.5 }}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Formas de pagamento</Typography>
                            <ChevronDown size={16} style={{ transform: showPaymentOptions ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                        </Box>
                    )}

                    {showPaymentOptions && (
                        <>
                            <PayMethodCard>
                                <PayMethodHead id="credit-card-payment-option" onClick={() => setShowCreditCard(!showCreditCard)}>
                                    <PayMethodIcon><CreditCard size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Cartão de crédito</Typography>
                                    <ChevronDown size={14} style={{ transform: showCreditCard ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={showCreditCard}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <TextField
                                                sx={{ ...standaloneFieldSx, width: '50%' }}
                                                label="Valor"
                                                placeholder="R$ 0,00"
                                                value={getCreditAmountString()}
                                                onChange={(e) => setCreditValue(formatCurrency(e.target.value))}
                                            />
                                            <TextField select sx={{ ...standaloneFloatingSelectSx, width: '50%' }} label="Maquininha" value={selectedMachine} onChange={(e) => setSelectedMachine(e.target.value)}>
                                                {machineCards.map(n => (
                                                    <MenuItem key={n.ID} value={n.ID}>{n.Name}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                        <TextField
                                            select
                                            fullWidth
                                            sx={standaloneFloatingSelectSx}
                                            label="Parcelas"
                                            value={selectedInstallments}
                                            disabled={installmentsLoading || installmentOptions.length === 0}
                                            onChange={(e) => setSelectedInstallments(Number(e.target.value))}
                                            helperText={
                                                installmentsLoading
                                                    ? 'Calculando parcelas…'
                                                    : installmentOptions.length === 0
                                                        ? 'Informe valor e maquininha válidos para ver as parcelas'
                                                        : ''
                                            }
                                        >
                                            {installmentOptions.map((opt) => (
                                                <MenuItem key={opt.count} value={opt.count}>
                                                    {opt.installmentLabel
                                                        ? `${opt.count}x ${opt.installmentLabel}`
                                                        : `${opt.count}x`}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            disabled={installmentsLoading || installmentOptions.length === 0}
                                            onClick={() => {
                                                const opt = installmentOptions.find((o) => o.count === Number(selectedInstallments));
                                                handleAddPayment(
                                                    'CRÉDITO',
                                                    getCreditAmountString(),
                                                    selectedInstallments,
                                                    opt?.installmentLabel ?? null
                                                );
                                            }}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>

                            <PayMethodCard>
                                <PayMethodHead id="debit-card-payment-option" onClick={() => setShowDebitCard(!showDebitCard)}>
                                    <PayMethodIcon><CreditCard size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Cartão de débito</Typography>
                                    <ChevronDown size={14} style={{ transform: showDebitCard ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={showDebitCard}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <TextField
                                                sx={{ ...standaloneFieldSx, width: '50%' }}
                                                label="Valor"
                                                placeholder="R$ 0,00"
                                                value={discount.applied && paymentType === 'single' ? discount.value : debitValue}
                                                onChange={(e) => setDebitValue(formatCurrency(e.target.value))}
                                            />
                                            <TextField select sx={{ ...standaloneFloatingSelectSx, width: '50%' }} label="Maquininha" value={selectedMachine} onChange={(e) => setSelectedMachine(e.target.value)}>
                                                {machineCards.map(n => (
                                                    <MenuItem key={n.ID} value={n.ID}>{n.Name}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => handleAddPayment('DÉBITO', discount.applied && paymentType === 'single' ? discount.value : debitValue, 1)}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>

                            <PayMethodCard>
                                <PayMethodHead id="pix-payment-option" onClick={() => setShowPix(!showPix)}>
                                    <PayMethodIcon><QrCode size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Pix</Typography>
                                    <ChevronDown size={14} style={{ transform: showPix ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={showPix}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <TextField
                                                sx={{ ...standaloneFieldSx, width: '50%' }}
                                                label="Valor"
                                                placeholder="R$ 0,00"
                                                value={discount.applied && paymentType === 'single' ? discount.value : pixValue}
                                                onChange={(e) => setPixValue(formatCurrency(e.target.value))}
                                            />
                                            <TextField select sx={{ ...standaloneFloatingSelectSx, width: '50%' }} label="Tipo" value={pixType} onChange={(e) => setPixType(e.target.value)}>
                                                <MenuItem value="PIXKEY">Pix Chave</MenuItem>
                                                <MenuItem value="PIXQRCODE">Pix QR Code</MenuItem>
                                            </TextField>
                                        </Box>
                                        {pixType === 'PIXQRCODE' && (
                                            <TextField select fullWidth sx={standaloneFloatingSelectSx} label="Maquininha" value={selectedMachine} onChange={(e) => setSelectedMachine(e.target.value)}>
                                                {machineCards.map(n => (
                                                    <MenuItem key={n.ID} value={n.ID}>{n.Name}</MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => handleAddPayment(pixType, discount.applied && paymentType === 'single' ? discount.value : pixValue, 1)}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>

                            <PayMethodCard>
                                <PayMethodHead id="cash-payment-option" onClick={() => setShowCash(!showCash)}>
                                    <PayMethodIcon><Banknote size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Dinheiro</Typography>
                                    <ChevronDown size={14} style={{ transform: showCash ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={showCash}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            sx={standaloneFieldSx}
                                            label="Valor recebido"
                                            placeholder="R$ 0,00"
                                            value={discount.applied && paymentType === 'single' ? discount.value : cashValue}
                                            onChange={(e) => setCashValue(formatCurrency(e.target.value))}
                                        />
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => handleAddPayment('DINHEIRO', discount.applied && paymentType === 'single' ? discount.value : cashValue, 1)}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>
                        </>
                    )}

                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, mt: 2, mb: 1 }}>Pagamentos adicionados</Typography>
                    <Box sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '12px', maxHeight: 220, overflowY: 'auto', p: payments.length ? 1 : 2 }}>
                        {payments.length === 0 && (
                            <Typography variant="caption" color="text.disabled">Nenhum pagamento adicionado ainda.</Typography>
                        )}
                        {payments.map((payment, index) => (
                            <PaidRow key={index}>
                                <PayMethodIcon sx={{ width: 28, height: 28, borderRadius: '8px' }}>
                                    {(payment.type === 'CRÉDITO' || payment.type === 'DÉBITO') && <CreditCard size={14} />}
                                    {(payment.type === 'PIXQRCODE' || payment.type === 'PIXKEY') && <QrCode size={14} />}
                                    {payment.type === 'DINHEIRO' && <Banknote size={14} />}
                                </PayMethodIcon>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                                        {payment.type === 'CRÉDITO' && 'Cartão de crédito'}
                                        {payment.type === 'DÉBITO' && 'Cartão de débito'}
                                        {payment.type === 'PIXQRCODE' && 'Pix QR Code'}
                                        {payment.type === 'PIXKEY' && 'Pix Chave'}
                                        {payment.type === 'DINHEIRO' && 'Dinheiro'}
                                    </Typography>
                                    {payment.type === 'CRÉDITO' && (
                                        <Typography variant="caption" color="text.secondary">
                                            {payment.installments}x
                                            {payment.installmentValueLabel ? ` ${payment.installmentValueLabel}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>R$ {payment.value}</Typography>
                                <IconButton size="small" onClick={() => handleRemovePayment(index)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                    <X size={14} />
                                </IconButton>
                            </PaidRow>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ px: 3, py: 2, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                    {errorMessage && (
                        <Alert
                            severity="error"
                            onClose={() => setErrorMessage('')}
                            sx={{
                                borderRadius: '10px',
                                mb: 2,
                                animation: 'slideDown 0.3s ease-out',
                                '@keyframes slideDown': {
                                    from: { opacity: 0, transform: 'translateY(-10px)' },
                                    to: { opacity: 1, transform: 'translateY(0)' }
                                }
                            }}
                        >
                            {errorMessage}
                        </Alert>
                    )}
                    <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        color="success"
                        disabled={payments.length === 0 || (payments.length > 1 && paymentType === 'multiple' && remainingValue !== '0,00') || isProcessingPayment}
                        onClick={() => setOpenDialog(true)}
                        startIcon={isProcessingPayment ? <CircularProgress size={18} color="inherit" /> : <CheckCircle2 size={18} />}
                        sx={{ height: 48, fontSize: 14 }}
                    >
                        {isProcessingPayment ? 'Processando...' : `Finalizar ${entityType === 'service_order' ? 'Ordem' : 'Venda'}`}
                    </Button>
                </Box>
            </Drawer>

            {/* Dialog de Confirmação */}
            <DialogForm
                iconType="warning"
                title="Atenção"
                content={entityType === 'service_order' ? 'Deseja realmente faturar esta ordem de serviço?' : 'Deseja realmente finalizar esta venda?'}
                confirmButtonText={entityType === 'service_order' ? 'Sim, faturar' : undefined}
                onSubmit={handleFinishPayment}
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                disabled={isProcessingPayment}
            />
        </>
    );
};

export default PaymentModal;
